
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import axios from "axios";

const BenefitSummary = () => {
  const { currentUser } = useSelector((state) => state.user || {});
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch benefits data
  useEffect(() => {
    if (!currentUser?._id) {
      toast.error("User not authenticated");
      setLoading(false);
      return;
    }

    const fetchBenefits = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}benefit/getUserBenefit`,
          { applicantId: currentUser._id },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setBenefits(response.data.data || []);
      } catch (err) {
        setError("Failed to load benefits");
        toast.error("Failed to load benefits");
      } finally {
        setLoading(false);
      }
    };
    fetchBenefits();
  }, [currentUser?._id]);

  // Helper function to get ISO week number
  const getWeekNumber = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  };

  // Group benefits by period
  const groupBenefits = (periodType) => {
    const grouped = {};
    benefits
      .filter((b) => b.benefitStatus === "Approved")
      .forEach((benefit) => {
        const date = new Date(benefit.createdAt);
        let key;
        if (periodType === "weekly") {
          const week = getWeekNumber(date);
          key = `${date.getFullYear()}-W${week}`;
        } else if (periodType === "monthly") {
          key = `${date.getFullYear()}-${date.getMonth() + 1}`;
        } else {
          key = date.getFullYear().toString();
        }

        if (!grouped[key]) {
          grouped[key] = { essential: 0, health: 0 };
        }
        const amount = parseFloat(benefit.amountRequested) || 0;
        grouped[key][benefit.benefitType] += amount;
      });

    return Object.entries(grouped).map(([key, totals]) => ({
      period: key,
      essential: totals.essential,
      health: totals.health,
      total: totals.essential + totals.health,
    })).sort((a, b) => a.period.localeCompare(b.period));
  };

  const weeklyBenefits = groupBenefits("weekly");
  const monthlyBenefits = groupBenefits("monthly");
  const yearlyBenefits = groupBenefits("yearly");

  if (loading) {
    return <div className="flex items-center justify-center h-screen bg-gray-100">Loading...</div>;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen text-red-500 bg-gray-100">
        {error}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Benefit Payment Summary</h2>

      {/* Weekly Benefits */}
      <h3 className="text-2xl font-semibold text-gray-700 mb-4">Weekly Benefits</h3>
      <div className="overflow-x-auto mb-8">
        <table className="w-full bg-gray-50 border border-gray-200">
          <thead className="bg-orange-400 text-white">
            <tr>
              <th className="py-3 px-4 text-left">Week</th>
              <th className="py-3 px-4 text-left">Essential (₦)</th>
              <th className="py-3 px-4 text-left">Health (₦)</th>
              <th className="py-3 px-4 text-left">Total (₦)</th>
            </tr>
          </thead>
          <tbody>
            {weeklyBenefits.length > 0 ? (
              weeklyBenefits.map((item, i) => (
                <tr key={i} className="border-t hover:bg-gray-100">
                  <td className="py-3 px-4">{item.period}</td>
                  <td className="py-3 px-4">{item.essential.toLocaleString()}</td>
                  <td className="py-3 px-4">{item.health.toLocaleString()}</td>
                  <td className="py-3 px-4">{item.total.toLocaleString()}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="py-3 px-4 text-center text-gray-500">
                  No weekly benefits available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Monthly Benefits */}
      <h3 className="text-2xl font-semibold text-gray-700 mb-4">Monthly Benefits</h3>
      <div className="overflow-x-auto mb-8">
        <table className="w-full bg-gray-50 border border-gray-200">
          <thead className="bg-orange-400 text-white">
            <tr>
              <th className="py-3 px-4 text-left">Month</th>
              <th className="py-3 px-4 text-left">Essential (₦)</th>
              <th className="py-3 px-4 text-left">Health (₦)</th>
              <th className="py-3 px-4 text-left">Total (₦)</th>
            </tr>
          </thead>
          <tbody>
            {monthlyBenefits.length > 0 ? (
              monthlyBenefits.map((item, i) => (
                <tr key={i} className="border-t hover:bg-gray-100">
                  <td className="py-3 px-4">{item.period}</td>
                  <td className="py-3 px-4">{item.essential.toLocaleString()}</td>
                  <td className="py-3 px-4">{item.health.toLocaleString()}</td>
                  <td className="py-3 px-4">{item.total.toLocaleString()}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="py-3 px-4 text-center text-gray-500">
                  No monthly benefits available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Yearly Benefits */}
      <h3 className="text-2xl font-semibold text-gray-700 mb-4">Yearly Benefits</h3>
      <div className="overflow-x-auto">
        <table className="w-full bg-gray-50 border border-gray-200">
          <thead className="bg-orange-400 text-white">
            <tr>
              <th className="py-3 px-4 text-left">Year</th>
              <th className="py-3 px-4 text-left">Essential (₦)</th>
              <th className="py-3 px-4 text-left">Health (₦)</th>
              <th className="py-3 px-4 text-left">Total (₦)</th>
            </tr>
          </thead>
          <tbody>
            {yearlyBenefits.length > 0 ? (
              yearlyBenefits.map((item, i) => (
                <tr key={i} className="border-t hover:bg-gray-100">
                  <td className="py-3 px-4">{item.period}</td>
                  <td className="py-3 px-4">{item.essential.toLocaleString()}</td>
                  <td className="py-3 px-4">{item.health.toLocaleString()}</td>
                  <td className="py-3 px-4">{item.total.toLocaleString()}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="py-3 px-4 text-center text-gray-500">
                  No yearly benefits available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BenefitSummary;
